import {
  AddPayrollResponse,
  AdjustmentSalaryPayload,
  ConfirmSalaryPayload,
  ContractSalary,
  Dependent,
  IAdjustmentSalary,
  IAdvanceFilters,
  IAdvanceList,
  IAllowanceList,
  IAllowanceYearByUserIdFilters,
  IBonusFilters,
  IBonusList,
  IContractSalaryFilters,
  IDependentFilters,
  ListFilterAllowance,
  ListParams,
  ListResponse,
  ListResponseAllowance,
  PayrollPayload,
  PayrollYear,
  ResetConfirmPayrollPayload,
  StatusTabPayroll,
  SuccessResponse,
  SuccessResponsePayrollYear,
} from 'types';
import axiosClient from './axiosClient';
import queryString from 'query-string';

const payrollApi = {
  importPayrollCsv(id: string, file: File, payload?: Record<string, any>): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    if (payload) {
      Object.keys(payload).forEach((key: string) => {
        formData.append(key, payload[key]);
      });
    }
    return axiosClient.post(`/payroll/details/${id}/import-csv`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  add(payload: PayrollPayload): Promise<SuccessResponse<AddPayrollResponse>> {
    return axiosClient.post('/payroll', payload);
  },

  getContractSalaryListByMonth(
    params: Partial<IContractSalaryFilters>,
  ): Promise<SuccessResponse<ListResponse<ContractSalary>>> {
    return axiosClient.get('/contract-configure/search', {
      params,
    });
  },

  getContractSalaryListByYear(
    params: Partial<IContractSalaryFilters>,
  ): Promise<SuccessResponse<ListResponse<ContractSalary>>> {
    return axiosClient.get('/contract-configure/salary-year', {
      params,
    });
  },

  exportContractSalaryByMonth(
    payload: Partial<IContractSalaryFilters>,
    userIds: string[],
  ): Promise<any> {
    return axiosClient.post(
      '/contract-configure/export',
      {
        ...payload,
        userIds,
      },
      {
        responseType: 'blob',
      },
    );
  },

  exportContractSalaryByYear(
    payload: Partial<IContractSalaryFilters>,
    userIds: string[],
  ): Promise<any> {
    return axiosClient.post(
      '/contract-configure/export-year',
      {
        ...payload,
        userIds,
      },
      {
        responseType: 'blob',
      },
    );
  },

  confirmContractSalary(payload: ConfirmSalaryPayload): Promise<SuccessResponse<any>> {
    return axiosClient.post('/confirm/payroll/salary', payload);
  },

  getAllUserHaveAllowance(
    params: Partial<ListFilterAllowance>,
  ): Promise<SuccessResponse<ListResponseAllowance<IAllowanceList>>> {
    return axiosClient.get('/allowance-details/search', {
      params: params,
    });
  },

  getAllowanceYearByUserId(
    params: Partial<IAllowanceYearByUserIdFilters>,
  ): Promise<SuccessResponse<ListResponseAllowance<IAllowanceList>>> {
    return axiosClient.get('/allowance-details/search-history', {
      params: params,
    });
  },

  getPayrollByYear(
    params: Partial<ListParams>,
  ): Promise<SuccessResponsePayrollYear<Array<PayrollYear>>> {
    return axiosClient.get('/payroll/payroll-by-year', {
      params,
    });
  },

  confirmAllowance(payload: ConfirmSalaryPayload): Promise<SuccessResponse<any>> {
    return axiosClient.post('/confirm/payroll/allowance', payload);
  },

  resetConfirmPayroll(payload: ResetConfirmPayrollPayload): Promise<SuccessResponse<any>> {
    return axiosClient.post('/confirm/cancel-payroll-tab', payload);
  },

  getAllUserHaveDependent(
    params: Partial<IDependentFilters>,
  ): Promise<SuccessResponse<ListResponse<Dependent>>> {
    return axiosClient.get('/dependant/search', {
      params,
    });
  },

  confirmDependent(payload: ConfirmSalaryPayload): Promise<SuccessResponse<any>> {
    return axiosClient.post('/confirm/payroll/dependant', payload);
  },

  getAllUserHaveBonus(
    params: Partial<IBonusFilters>,
  ): Promise<SuccessResponse<ListResponse<IBonusList>>> {
    return axiosClient.get('/bonus-details/search', {
      params,
    });
  },

  getAdjustmentSalaryList(
    params: Partial<AdjustmentSalaryPayload>,
  ): Promise<SuccessResponse<ListResponse<IAdjustmentSalary>>> {
    return axiosClient.get('/user-adjust-salary/search', {
      params,
    });
  },

  confirmBonus(payload: ConfirmSalaryPayload): Promise<SuccessResponse<any>> {
    return axiosClient.post('/confirm/payroll/bonus', payload);
  },

  getStatusTabPayroll(monthYear: string): Promise<SuccessResponse<StatusTabPayroll>> {
    return axiosClient.get('/confirm/check-confirm', {
      params: { monthYear },
    });
  },

  getAllUserHaveAdvance(
    params: Partial<IAdvanceFilters>,
  ): Promise<SuccessResponse<ListResponse<IAdvanceList>>> {
    return axiosClient.get('/user-advance/search', {
      params,
    });
  },

  confirmAdvance(payload: ConfirmSalaryPayload): Promise<SuccessResponse<any>> {
    return axiosClient.post('/confirm/payroll/advance', payload);
  },

  checkConfirmAdjustmentSalary(params: {
    monthYear: string;
    type: string;
  }): Promise<SuccessResponse<{ isConfirm: boolean }>> {
    return axiosClient.get('/confirm/check-confirm-type', {
      params,
    });
  },

  confirmAdjustmentSalary(payload: {
    monthYear: string;
    status: string;
  }): Promise<SuccessResponse<any>> {
    return axiosClient.post('/confirm/payroll/adjust', payload);
  },

  exportAdjustmentSalary(payload: { monthYear: string; userIds: string[] }): Promise<any> {
    return axiosClient.post('/user-adjust-salary/export', payload, {
      responseType: 'blob',
    });
  },

  createPayrollDetails(payload: { monthYear: string; workFlowTypeId: number }): Promise<any> {
    console.log(payload);
    return axiosClient.post('/confirm/create-payroll', payload);
  },

  getDetailPayslipMonth(monthYear: string): Promise<any> {
    return axiosClient.get(`payroll/view-payslip?${queryString.stringify({ monthYear })}`);
  },

  alertUseWorkflow: (params: { workFlowTypeId: string }) => {
    const response = axiosClient.get(
      `/payroll/alert-use-workflow?${queryString.stringify(params)}`,
    );
    return response;
  },

  exportDependent(payload: { monthYear: string; employeeIds: string[] }): Promise<any> {
    return axiosClient.post('/dependant/export-dependant-user', payload, {
      responseType: 'blob',
    });
  },

  sendOTPViewPayroll(payload: { email: string; type: 'insurance' | 'payroll' }): Promise<any> {
    return axiosClient.post('/user-otp/send-otp-view-money', payload);
  },

  verifyOTPViewPayroll(payload: { id: string; otp: string; userId: string }): Promise<any> {
    return axiosClient.post('/user-otp/verify-otp-view-money', payload);
  },
  getTotalAdvance: async ({ userIds, months }: { userIds: string[]; months: string[] }) => {
    try {
      // Thử POST trước (tốt hơn cho data lớn)
      return await axiosClient.post('/user-advance/total-advance', {
        userIds,
        months,
      });
    } catch (error: any) {
      // Nếu POST chưa có, fallback về GET
      if (error?.response?.status === 404 || error?.response?.status === 405) {
        console.warn('POST not available, falling back to GET');
        const params = new URLSearchParams();
        params.set('userIds', userIds.join(','));
        params.set('months', months.join(','));
        return axiosClient.get(`/user-advance/total-advance?${params.toString()}`);
      }
      throw error;
    }
  },

  getPayslipRecipients: (body: { userIds: string[]; period?: string; payrollDetailsId?: number }) =>
    axiosClient.post('/payroll/send-payslip-manual/recipients', body) as Promise<
      SuccessResponse<
        {
          userId: string;
          employeeId: string;
          fullName: string;
          isOffboarded: boolean;
          email: string | null;
          source: 'company' | 'personal';
          canSend: boolean;
        }[]
      >
    >,

  suggestPayRunName: (params: { payrollGroupId: number; payRunType: string }) =>
    axiosClient.get(`/payroll/details/suggest-name?${queryString.stringify(params)}`) as Promise<
      SuccessResponse<{ name: string; runNumber: number; companyName: string }>
    >,

  getApprovalProgress: (id: number | string) =>
    axiosClient.get(`/payroll/details/${id}/approval-progress`) as Promise<SuccessResponse<any>>,

  getPayrollSummary: (id: number | string) =>
    axiosClient.get(`/payroll/details/${id}/summary`) as Promise<
      SuccessResponse<{ totals: any; summaryRecord: any }>
    >,
};

export default payrollApi;
